import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  getIncomeStatement,
  getBalanceSheet,
  getCashFlowStatement,
  getRatios,
  getFinancialRatios,
  getEarnings,
  getEarningsCallTranscripts,
  getAnalystRating,
  getAnalystRatingHistory,
  getDividendSummary,
  getDividendHistory,
  getBeneficialOwnership,
  getHistoricalPriceEod,
  getBulkProfile,
  getInsiderTrading,
  getRankingScore,
  getRating,
  getDividendsBulk,
  getBulkIncomeStatement,
  getBulkBalanceSheet,
  getBulkCashFlowStatement,
  getBulkAnnualizedReturn,
  getFinancialAnalytics,
  getFinancialAnalyticsList,
  getMarginOfSafety,
  getSecFilingsBySector,
  getSwotScore,
  getKeyStats,
  getStockNews,
  getCompanyProfile,
  getQuotes,
  getHistoricalDividends,
  getHistoricalSectorPerformance,
  getStockAnalysis,
  getAnalystDescription,
  getAnalystRatingDescription,
  getSnaptradeAccountHoldings,
  getSnaptradeAccounts,
  getSnaptradeLoginLink,
  refreshSnaptradeAccount,
  searchTradableSymbols,
  getTradingBalances,
  previewTradeOrder,
  placeTradeOrder,
  listTradeOrders,
  cancelTradeOrder,
} from "./financial-integration.api";
import { LIVE_OPEN_ORDER_STATUSES } from "./financial-integration.type";
import type {
  GetIncomeStatementQuery,
  GetIncomeStatementResponse,
  GetBalanceSheetQuery,
  GetBalanceSheetResponse,
  GetCashFlowStatementQuery,
  GetCashFlowStatementResponse,
  GetRatiosQuery,
  GetRatiosResponse,
  GetFinancialRatiosQuery,
  GetFinancialRatiosResponse,
  GetEarningsQuery,
  GetEarningsResponse,
  GetEarningsCallTranscriptQuery,
  GetEarningsCallTranscriptResponse,
  GetAnalystRatingQuery,
  GetAnalystRatingResponse,
  GetAnalystRatingHistoryQuery,
  GetAnalystRatingHistoryResponse,
  GetDividendSummaryQuery,
  GetDividendSummaryResponse,
  GetDividendHistoryQuery,
  GetDividendHistoryResponse,
  GetBeneficialOwnershipQuery,
  GetBeneficialOwnershipResponse,
  GetHistoricalPriceEodQuery,
  GetHistoricalPriceEodResponse,
  BulkProfileRequest,
  BulkProfileResponse,
  GetInsiderTradingQuery,
  GetInsiderTradingResponse,
  RankingScoreRequest,
  RankingScoreResponse,
  RatingRequest,
  RatingResponse,
  DividendsBulkRequest,
  DividendsBulkResponse,
  BulkIncomeStatementRequest,
  BulkIncomeStatementResponse,
  BulkBalanceSheetRequest,
  BulkBalanceSheetResponse,
  BulkCashFlowStatementRequest,
  BulkCashFlowStatementResponse,
  BulkAnnualizedReturnRequest,
  BulkAnnualizedReturnResponse,
  GetFinancialAnalyticsQuery,
  GetFinancialAnalyticsResponse,
  GetFinancialAnalyticsListQuery,
  GetFinancialAnalyticsListResponse,
  GetMarginOfSafetyQuery,
  GetSecFilingsBySectorQuery,
  GetSecFilingsBySectorResponse,
  GetSwotScoreQuery,
  GetSwotScoreResponse,
  GetKeyStatsQuery,
  GetKeyStatsResponse,
  GetStockNewsQuery,
  GetStockNewsResponse,
  GetCompanyProfileQuery,
  GetCompanyProfileResponse,
  GetQuotesResponse,
  GetHistoricalDividendsQuery,
  GetHistoricalDividendsResponse,
  GetHistoricalSectorPerformanceQuery,
  GetHistoricalSectorPerformanceResponse,
  GetStockAnalysisQuery,
  GetStockAnalysisResponse,
  GetAnalystDescriptionQuery,
  GetAnalystDescriptionResponse,
  GetAnalystRatingDescriptionQuery,
  GetAnalystRatingDescriptionResponse,
  GetSnaptradeAccountHoldingsResponse,
  GetSnaptradeAccountsResponse,
  RefreshSnaptradeAccountResponse,
  PlaceTradeOrderBody,
  SnaptradeLoginResponse,
} from "./financial-integration.type";

// Query keys
export const financialIntegrationKeys = {
  all: ["financial-integration"] as const,
  incomeStatement: (query: GetIncomeStatementQuery) =>
    [...financialIntegrationKeys.all, "income-statement", query] as const,
  balanceSheet: (query: GetBalanceSheetQuery) =>
    [...financialIntegrationKeys.all, "balance-sheet", query] as const,
  cashFlowStatement: (query: GetCashFlowStatementQuery) =>
    [...financialIntegrationKeys.all, "cash-flow-statement", query] as const,
  ratios: (query: GetRatiosQuery) =>
    [...financialIntegrationKeys.all, "ratios", query] as const,
  financialRatios: (query: GetFinancialRatiosQuery) =>
    [...financialIntegrationKeys.all, "financial-ratios", query] as const,
  earnings: (query: GetEarningsQuery) =>
    [...financialIntegrationKeys.all, "earnings", query] as const,
  earningsCallTranscripts: (query: GetEarningsCallTranscriptQuery) =>
    [
      ...financialIntegrationKeys.all,
      "earning-call-transcript",
      query,
    ] as const,
  analystRating: (query: GetAnalystRatingQuery) =>
    [...financialIntegrationKeys.all, "analyst-rating", query] as const,
  analystRatingHistory: (query: GetAnalystRatingHistoryQuery) =>
    [...financialIntegrationKeys.all, "analyst-rating-history", query] as const,
  dividendSummary: (query: GetDividendSummaryQuery) =>
    [...financialIntegrationKeys.all, "dividents-summary", query] as const,
  dividendHistory: (query: GetDividendHistoryQuery) =>
    [...financialIntegrationKeys.all, "dividends", query] as const,
  beneficialOwnership: (query: GetBeneficialOwnershipQuery) =>
    [...financialIntegrationKeys.all, "beneficial-ownership", query] as const,
  historicalPriceEod: (query: GetHistoricalPriceEodQuery) =>
    [...financialIntegrationKeys.all, "historical-price-eod", query] as const,
  bulkProfile: (payload: BulkProfileRequest) =>
    [...financialIntegrationKeys.all, "bulk-profile", payload] as const,
  insiderTrading: (query: GetInsiderTradingQuery) =>
    [...financialIntegrationKeys.all, "insider-trading", query] as const,
  rankingScore: (payload: RankingScoreRequest) =>
    [...financialIntegrationKeys.all, "ranking-score", payload] as const,
  rating: (payload: RatingRequest) =>
    [...financialIntegrationKeys.all, "rating", payload] as const,
  dividendsBulk: (payload: DividendsBulkRequest) =>
    [...financialIntegrationKeys.all, "dividends-bulk", payload] as const,
  incomeStatementBulk: (payload: BulkIncomeStatementRequest) =>
    [
      ...financialIntegrationKeys.all,
      "income-statement-bulk",
      payload,
    ] as const,
  balanceSheetBulk: (payload: BulkBalanceSheetRequest) =>
    [...financialIntegrationKeys.all, "balance-sheet-bulk", payload] as const,
  cashFlowBulk: (payload: BulkCashFlowStatementRequest) =>
    [...financialIntegrationKeys.all, "cash-flow-bulk", payload] as const,
  annualizedReturnBulk: (payload: BulkAnnualizedReturnRequest) =>
    [
      ...financialIntegrationKeys.all,
      "annualized-return-bulk",
      payload,
    ] as const,
  financialAnalytics: (query: GetFinancialAnalyticsQuery) =>
    [...financialIntegrationKeys.all, "financial-analytics", query] as const,
  financialAnalyticsList: (query: GetFinancialAnalyticsListQuery) =>
    [
      ...financialIntegrationKeys.all,
      "financial-analytics-list",
      query,
    ] as const,
  snaptradeAccounts: () =>
    [...financialIntegrationKeys.all, "snaptrade-accounts"] as const,
  snaptradeAccountHoldings: (accountId: string) =>
    [...financialIntegrationKeys.all, "snaptrade-account-holdings", accountId] as const,
  tradingBalances: (accountId: string) =>
    [...financialIntegrationKeys.all, "trading-balances", accountId] as const,
  tradableSymbols: (accountId: string, query: string) =>
    [...financialIntegrationKeys.all, "tradable-symbols", accountId, query] as const,
  tradeOrders: (accountId: string) =>
    [...financialIntegrationKeys.all, "trade-orders", accountId] as const,
};

// Get income statement query
export const useIncomeStatement = (
  query: GetIncomeStatementQuery,
  options?: Omit<
    UseQueryOptions<GetIncomeStatementResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.incomeStatement(query),
    queryFn: () => getIncomeStatement(query),
    enabled: (options?.enabled ?? true) && !!query.symbol && !!query.period,
    ...options,
  });
};

// Get balance sheet query
export const useBalanceSheet = (
  query: GetBalanceSheetQuery,
  options?: Omit<
    UseQueryOptions<GetBalanceSheetResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.balanceSheet(query),
    queryFn: () => getBalanceSheet(query),
    enabled: (options?.enabled ?? true) && !!query.symbol && !!query.period,
    ...options,
  });
};

export const useSnaptradeAccounts = (
  options?: Omit<
    UseQueryOptions<GetSnaptradeAccountsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.snaptradeAccounts(),
    queryFn: () => getSnaptradeAccounts(),
    ...options,
  });
};

export const useSnaptradeAccountHoldings = (
  accountId: string | undefined,
  options?: Omit<
    UseQueryOptions<GetSnaptradeAccountHoldingsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.snaptradeAccountHoldings(accountId || ""),
    queryFn: () =>
      getSnaptradeAccountHoldings(accountId!, { page: 1, limit: 100 }),
    enabled: (options?.enabled ?? true) && !!accountId,
    ...options,
  });
};

export const useSnaptradeLogin = (
  options?: Omit<
    UseMutationOptions<SnaptradeLoginResponse, Error, void>,
    "mutationFn"
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => getSnaptradeLoginLink(),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: financialIntegrationKeys.snaptradeAccounts(),
      });
      options?.onSuccess?.(...args);
    },
    ...options,
  });
};

export const useRefreshSnaptradeAccount = (
  options?: Omit<
    UseMutationOptions<RefreshSnaptradeAccountResponse, Error, string>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (accountId: string) => refreshSnaptradeAccount(accountId),
    onSuccess: (data, accountId, onMutateResult, context) => {
      void queryClient.fetchQuery({
        queryKey: financialIntegrationKeys.snaptradeAccountHoldings(accountId),
        queryFn: () =>
          getSnaptradeAccountHoldings(accountId, {
            updateCache: true,
            page: 1,
            limit: 100,
          }),
      });
      void queryClient.fetchQuery({
        queryKey: financialIntegrationKeys.snaptradeAccounts(),
        queryFn: () => getSnaptradeAccounts({ updateCache: true }),
      });
      options?.onSuccess?.(data, accountId, onMutateResult, context);
    },
  });
};

// Get cash flow statement query
export const useCashFlowStatement = (
  query: GetCashFlowStatementQuery,
  options?: Omit<
    UseQueryOptions<GetCashFlowStatementResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.cashFlowStatement(query),
    queryFn: () => getCashFlowStatement(query),
    enabled: (options?.enabled ?? true) && !!query.symbol && !!query.period,
    ...options,
  });
};

// Get ratios query
export const useRatios = (
  query: GetRatiosQuery,
  options?: Omit<
    UseQueryOptions<GetRatiosResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.ratios(query),
    queryFn: () => getRatios(query),
    enabled: (options?.enabled ?? true) && !!query.symbol && !!query.period,
    ...options,
  });
};

// Get financial ratios query
export const useFinancialRatios = (
  query: GetFinancialRatiosQuery,
  options?: Omit<
    UseQueryOptions<GetFinancialRatiosResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, period, tabAccessToken } = query;
  return useQuery({
    queryKey: financialIntegrationKeys.financialRatios(query),
    queryFn: () => getFinancialRatios(query),
    enabled:
      (options?.enabled ?? true) && !!symbol && !!period && !!tabAccessToken,
    ...options,
  });
};

// Get earnings query
export const useEarnings = (
  query: GetEarningsQuery,
  options?: Omit<
    UseQueryOptions<GetEarningsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.earnings(query),
    queryFn: () => getEarnings(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

// Get earnings call transcripts query
export const useEarningsCallTranscripts = (
  query: GetEarningsCallTranscriptQuery,
  options?: Omit<
    UseQueryOptions<GetEarningsCallTranscriptResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.earningsCallTranscripts(query),
    queryFn: () => getEarningsCallTranscripts(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

// Get analyst rating query
export const useAnalystRating = (
  query: GetAnalystRatingQuery,
  options?: Omit<
    UseQueryOptions<GetAnalystRatingResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;
  return useQuery({
    queryKey: financialIntegrationKeys.analystRating(query),
    queryFn: () => getAnalystRating(query),
    enabled: (options?.enabled ?? true) && !!symbol && !!tabAccessToken,
    ...options,
  });
};

// Get analyst rating history query
export const useAnalystRatingHistory = (
  query: GetAnalystRatingHistoryQuery,
  options?: Omit<
    UseQueryOptions<GetAnalystRatingHistoryResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, page, limit, tabAccessToken } = query;
  return useQuery({
    queryKey: financialIntegrationKeys.analystRatingHistory(query),
    queryFn: () => getAnalystRatingHistory(query),
    // Keep the rows while another page of the same symbol loads.
    placeholderData: (previous, previousQuery) =>
      (previousQuery?.queryKey.at(-1) as GetAnalystRatingHistoryQuery | undefined)
        ?.symbol === symbol
        ? previous
        : undefined,
    enabled:
      (options?.enabled ?? true) &&
      !!symbol &&
      Number.isInteger(page) &&
      page >= 0 &&
      Number.isInteger(limit) &&
      limit > 0 &&
      !!tabAccessToken,
    ...options,
  });
};

// Get dividend summary query
export const useDividendSummary = (
  query: GetDividendSummaryQuery,
  options?: Omit<
    UseQueryOptions<GetDividendSummaryResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.dividendSummary(query),
    queryFn: () => getDividendSummary(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

// Get dividend history query
export const useDividendHistory = (
  query: GetDividendHistoryQuery,
  options?: Omit<
    UseQueryOptions<GetDividendHistoryResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.dividendHistory(query),
    queryFn: () => getDividendHistory(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

// Get beneficial ownership query
export const useBeneficialOwnership = (
  query: GetBeneficialOwnershipQuery,
  options?: Omit<
    UseQueryOptions<GetBeneficialOwnershipResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.beneficialOwnership(query),
    queryFn: () => getBeneficialOwnership(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

// Get historical price EOD query
export const useHistoricalPriceEod = (
  query: GetHistoricalPriceEodQuery,
  options?: Omit<
    UseQueryOptions<GetHistoricalPriceEodResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.historicalPriceEod(query),
    queryFn: () => getHistoricalPriceEod(query),
    enabled:
      (options?.enabled ?? true) &&
      !!query.symbol &&
      Boolean(query.from) &&
      Boolean(query.to),
    ...options,
  });
};

export const useBulkProfile = (
  payload: BulkProfileRequest,
  options?: Omit<
    UseQueryOptions<BulkProfileResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.bulkProfile(payload),
    queryFn: () => getBulkProfile(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useInsiderTrading = (
  query: GetInsiderTradingQuery,
  options?: Omit<
    UseQueryOptions<GetInsiderTradingResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const isPaginationValid =
    typeof query.page === "number" &&
    Number.isInteger(query.page) &&
    query.page >= 0 &&
    typeof query.limit === "number" &&
    Number.isInteger(query.limit) &&
    query.limit > 0;

  return useQuery({
    queryKey: financialIntegrationKeys.insiderTrading(query),
    queryFn: () => getInsiderTrading(query),
    enabled: (options?.enabled ?? true) && !!query.symbol && isPaginationValid,
    ...options,
  });
};

export const useRankingScore = (
  payload: RankingScoreRequest,
  options?: Omit<
    UseQueryOptions<RankingScoreResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.rankingScore(payload),
    queryFn: () => getRankingScore(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useRating = (
  payload: RatingRequest,
  options?: Omit<UseQueryOptions<RatingResponse, Error>, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.rating(payload),
    queryFn: () => getRating(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useDividendsBulk = (
  payload: DividendsBulkRequest,
  options?: Omit<
    UseQueryOptions<DividendsBulkResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.dividendsBulk(payload),
    queryFn: () => getDividendsBulk(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useBulkIncomeStatement = (
  payload: BulkIncomeStatementRequest,
  options?: Omit<
    UseQueryOptions<BulkIncomeStatementResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.incomeStatementBulk(payload),
    queryFn: () => getBulkIncomeStatement(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useBulkBalanceSheet = (
  payload: BulkBalanceSheetRequest,
  options?: Omit<
    UseQueryOptions<BulkBalanceSheetResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.balanceSheetBulk(payload),
    queryFn: () => getBulkBalanceSheet(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useBulkCashFlowStatement = (
  payload: BulkCashFlowStatementRequest,
  options?: Omit<
    UseQueryOptions<BulkCashFlowStatementResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.cashFlowBulk(payload),
    queryFn: () => getBulkCashFlowStatement(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useBulkAnnualizedReturn = (
  payload: BulkAnnualizedReturnRequest,
  options?: Omit<
    UseQueryOptions<BulkAnnualizedReturnResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.annualizedReturnBulk(payload),
    queryFn: () => getBulkAnnualizedReturn(payload),
    enabled:
      (options?.enabled ?? true) &&
      Array.isArray(payload.symbols) &&
      payload.symbols.length > 0,
    ...options,
  });
};

export const useFinancialAnalytics = (
  query: GetFinancialAnalyticsQuery,
  options?: Omit<
    UseQueryOptions<GetFinancialAnalyticsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.financialAnalytics(query),
    queryFn: () => getFinancialAnalytics(query),
    enabled: (options?.enabled ?? true) && !!query.symbol,
    ...options,
  });
};

export const useFinancialAnalyticsList = (
  query: GetFinancialAnalyticsListQuery = {},
  options?: Omit<
    UseQueryOptions<GetFinancialAnalyticsListResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.financialAnalyticsList(query),
    queryFn: () => getFinancialAnalyticsList(query),
    ...options,
  });
};

const marginOfSafetyKeys = {
  all: ["margin-of-safety"] as const,
  detail: (symbol: string) => [...marginOfSafetyKeys.all, symbol] as const,
};

export const useMarginOfSafety = (
  query: GetMarginOfSafetyQuery,
  options?: { enabled?: boolean }
) => {
  const { symbol, tabAccessToken } = query;

  return useQuery({
    queryKey: marginOfSafetyKeys.detail(symbol),
    queryFn: () => getMarginOfSafety(query),
    enabled:
      options?.enabled !== false && Boolean(symbol) && Boolean(tabAccessToken),
  });
};

const secFilingsBySectorKeys = {
  all: ["sec-filings-by-sector"] as const,
  detail: (ticker: string, year: number) =>
    [...secFilingsBySectorKeys.all, ticker, year] as const,
};

export const useSecFilingsBySector = (
  query: GetSecFilingsBySectorQuery,
  options?: Omit<
    UseQueryOptions<GetSecFilingsBySectorResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { ticker, year } = query;

  return useQuery({
    queryKey: secFilingsBySectorKeys.detail(ticker, year),
    queryFn: () => getSecFilingsBySector(query),
    enabled: (options?.enabled ?? true) && Boolean(ticker) && Boolean(year),
    ...options,
  });
};

const swotScoreKeys = {
  all: ["swot-score"] as const,
  detail: (symbol: string) => [...swotScoreKeys.all, symbol] as const,
};

export const useSwotScore = (
  query: GetSwotScoreQuery,
  options?: Omit<
    UseQueryOptions<GetSwotScoreResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;

  return useQuery({
    queryKey: swotScoreKeys.detail(symbol),
    queryFn: () => getSwotScore(query),
    enabled:
      (options?.enabled ?? true) && Boolean(symbol) && Boolean(tabAccessToken),
    ...options,
  });
};

const keyStatsKeys = {
  all: ["key-stats"] as const,
  detail: (symbol: string) => [...keyStatsKeys.all, symbol] as const,
};

export const useKeyStats = (
  query: GetKeyStatsQuery,
  options?: Omit<
    UseQueryOptions<GetKeyStatsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;

  return useQuery({
    queryKey: keyStatsKeys.detail(symbol),
    queryFn: () => getKeyStats(query),
    enabled:
      (options?.enabled ?? true) && Boolean(symbol) && Boolean(tabAccessToken),
    ...options,
  });
};

const stockNewsKeys = {
  all: ["stock-news"] as const,
  list: (query: GetStockNewsQuery) => [...stockNewsKeys.all, query] as const,
};

export const useStockNews = (
  query: GetStockNewsQuery,
  options?: Omit<
    UseQueryOptions<GetStockNewsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: stockNewsKeys.list(query),
    queryFn: () => getStockNews(query),
    enabled:
      (options?.enabled ?? true) &&
      Boolean(query.symbols) &&
      (query.page === undefined || query.page >= 0) &&
      (query.limit === undefined || query.limit > 0),
    ...options,
  });
};

const companyProfileKeys = {
  all: ["company-profile"] as const,
  detail: (symbol: string) => [...companyProfileKeys.all, symbol] as const,
};

export const useCompanyProfile = (
  query: GetCompanyProfileQuery,
  options?: Omit<
    UseQueryOptions<GetCompanyProfileResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;

  return useQuery({
    queryKey: companyProfileKeys.detail(symbol),
    queryFn: () => getCompanyProfile(query),
    enabled:
      (options?.enabled ?? true) &&
      Boolean(symbol) &&
      Boolean(tabAccessToken),
    ...options,
  });
};

const quotesKeys = {
  all: ["quotes"] as const,
  list: () => [...quotesKeys.all] as const,
};

export const useQuotes = (
  options?: Omit<
    UseQueryOptions<GetQuotesResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: quotesKeys.list(),
    queryFn: () => getQuotes(),
    ...options,
  });
};

const historicalDividendsKeys = {
  all: ["historical-dividends"] as const,
  detail: (query: GetHistoricalDividendsQuery) =>
    [...historicalDividendsKeys.all, query] as const,
};

export const useHistoricalDividends = (
  query: GetHistoricalDividendsQuery,
  options?: Omit<
    UseQueryOptions<GetHistoricalDividendsResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: historicalDividendsKeys.detail(query),
    queryFn: () => getHistoricalDividends(query),
    enabled: (options?.enabled ?? true) && Boolean(query.symbol),
    ...options,
  });
};

const historicalSectorPerformanceKeys = {
  all: ["historical-sector-performance"] as const,
  detail: (query: GetHistoricalSectorPerformanceQuery) =>
    [...historicalSectorPerformanceKeys.all, query] as const,
};

export const useHistoricalSectorPerformance = (
  query: GetHistoricalSectorPerformanceQuery,
  options?: Omit<
    UseQueryOptions<GetHistoricalSectorPerformanceResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: historicalSectorPerformanceKeys.detail(query),
    queryFn: () => getHistoricalSectorPerformance(query),
    ...options,
  });
};

const stockAnalysisKeys = {
  all: ["stock-analysis"] as const,
  detail: (query: GetStockAnalysisQuery) =>
    [...stockAnalysisKeys.all, query] as const,
};

export const useStockAnalysis = (
  query: GetStockAnalysisQuery,
  options?: Omit<
    UseQueryOptions<GetStockAnalysisResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;
  return useQuery({
    queryKey: stockAnalysisKeys.detail(query),
    queryFn: () => getStockAnalysis(query),
    enabled: !!symbol && !!tabAccessToken,
    ...options,
  });
};

// Analyst Description query keys
export const analystDescriptionKeys = {
  all: ["analyst-description"] as const,
  detail: (query: GetAnalystDescriptionQuery) =>
    [...analystDescriptionKeys.all, query.symbol] as const,
};

export const useAnalystDescription = (
  query: GetAnalystDescriptionQuery,
  options?: Omit<
    UseQueryOptions<GetAnalystDescriptionResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;
  return useQuery({
    queryKey: analystDescriptionKeys.detail(query),
    queryFn: () => getAnalystDescription(query),
    enabled: !!symbol && !!tabAccessToken,
    ...options,
  });
};

const analystRatingDescriptionKeys = {
  all: ["analyst-rating-description"] as const,
  detail: (symbol: string) =>
    [...analystRatingDescriptionKeys.all, symbol] as const,
};

export const useAnalystRatingDescription = (
  query: GetAnalystRatingDescriptionQuery,
  options?: Omit<
    UseQueryOptions<GetAnalystRatingDescriptionResponse, Error>,
    "queryKey" | "queryFn"
  >
) => {
  const { symbol, tabAccessToken } = query;
  return useQuery({
    queryKey: analystRatingDescriptionKeys.detail(symbol),
    queryFn: () => getAnalystRatingDescription(query),
    enabled:
      (options?.enabled ?? true) && Boolean(symbol) && Boolean(tabAccessToken),
    ...options,
  });
};

const invalidateLiveTrading = (
  queryClient: ReturnType<typeof useQueryClient>,
  accountId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: financialIntegrationKeys.tradingBalances(accountId),
  });
  queryClient.invalidateQueries({
    queryKey: financialIntegrationKeys.tradeOrders(accountId),
  });
  queryClient.invalidateQueries({
    queryKey: financialIntegrationKeys.snaptradeAccountHoldings(accountId),
  });
};

export const useTradingBalances = (
  accountId: string | undefined,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.tradingBalances(accountId || ""),
    queryFn: () => getTradingBalances(accountId!),
    enabled: (options?.enabled ?? true) && Boolean(accountId),
    retry: false,
  });
};

export const useTradableSymbols = (
  accountId: string | undefined,
  query: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: financialIntegrationKeys.tradableSymbols(accountId || "", query),
    queryFn: () => searchTradableSymbols(accountId!, query),
    enabled:
      (options?.enabled ?? true) &&
      Boolean(accountId) &&
      query.trim().length > 0,
  });
};

export const useTradeOrders = (
  accountId: string | undefined,
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: financialIntegrationKeys.tradeOrders(accountId || ""),
    queryFn: async () => {
      const next = await listTradeOrders(accountId!, { page: 1, limit: 50 });
      const previous = queryClient.getQueryData<
        Awaited<ReturnType<typeof listTradeOrders>>
      >(financialIntegrationKeys.tradeOrders(accountId || ""));
      const hadOpen = (previous?.data ?? []).some((order) =>
        LIVE_OPEN_ORDER_STATUSES.has(order.status),
      );
      const hasOpen = (next.data ?? []).some((order) =>
        LIVE_OPEN_ORDER_STATUSES.has(order.status),
      );
      // While an order is open, re-read holdings. That stays a cache hit until
      // an order webhook deletes the 24-hour entry, then the next read loads
      // the filled position. Balances are fetched only once the order leaves
      // the open set, because they are not cached.
      if (accountId && (hasOpen || hadOpen)) {
        void queryClient.invalidateQueries({
          queryKey: financialIntegrationKeys.snaptradeAccountHoldings(accountId),
        });
        if (hadOpen && !hasOpen) {
          void queryClient.invalidateQueries({
            queryKey: financialIntegrationKeys.tradingBalances(accountId),
          });
        }
      }
      return next;
    },
    enabled: (options?.enabled ?? true) && Boolean(accountId),
    refetchInterval: (query) => {
      const orders = query.state.data?.data ?? [];
      return orders.some((order) => LIVE_OPEN_ORDER_STATUSES.has(order.status))
        ? 15000
        : false;
    },
  });
};

export const usePreviewTradeOrder = () => {
  return useMutation({
    mutationFn: ({
      accountId,
      body,
    }: {
      accountId: string;
      body: Omit<PlaceTradeOrderBody, "idempotency_key" | "preview_id">;
    }) => previewTradeOrder(accountId, body),
  });
};

export const usePlaceTradeOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      accountId,
      body,
    }: {
      accountId: string;
      body: PlaceTradeOrderBody;
    }) => placeTradeOrder(accountId, body),
    onSuccess: (_data, variables) => {
      invalidateLiveTrading(queryClient, variables.accountId);
    },
  });
};

export const useCancelTradeOrder = (accountId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => cancelTradeOrder(orderId),
    onSuccess: () => {
      if (accountId) {
        invalidateLiveTrading(queryClient, accountId);
      }
    },
  });
};
